/*
 * The Private License
 *
 * Copyright (c) 2023 Mahon Media LLC.
 */

import React, {forwardRef, useState} from "react";
import FormControl from "@mui/material/FormControl";
import {Button, CircularProgress, InputAdornment, TextField, Dialog, DialogTitle, DialogContent, DialogActions} from "@mui/material";
import {useNotification} from "../contexts/NotificationContext";
import Cookies from "js-cookie";
import {enoughAvailability} from "../utils/enoughAvailability";
import {lookupUpgradePrice} from "../utils/roomCodes";
import {getBackendBaseUrl} from "../utils/envUtils";

const UpgradeRoom = forwardRef(
  ({selected, from, to, env, confirm, cancel, loading, availability}, ref) => {
    const {setIsOpen, setIsFailed, setMessage} = useNotification();
    const [load, setLoading] = useState(false);

    // Dialogue Stuff
    const [open, setDialogOpen] = useState(false);
    const handleClickDialogOpen = () => setDialogOpen(true);
    const handleClickDialogClose = () => setDialogOpen(false);

    const [deactivateEmail, setDeactivateEmail] = useState(false);
    const token = Cookies.get("jwtToken");

    function mapReservationsToPrices(reservations) {
      const result = {}
      reservations.forEach(reservation => {
        result[reservation.rid] = {}
        to.forEach(roomCode => {
          result[reservation.rid][roomCode.roomType] = lookupUpgradePrice(reservation.roomType, roomCode.roomType)
        });
      });
      return result;
    }
    const sendEmail = (event) => {
      event.preventDefault();
      setLoading(true);
      const prices = mapReservationsToPrices(selected);
      console.log("Prices for are ", prices);
      const notifications = selected.map((customer) => ({
        "rid": customer.rid.trim(),
        "cid": customer.cid,
        "surname": customer.surname,
        "rate": customer.rateAmount,
        // "email": customer.email,
        "email": "jschein@mahon.media",
        "room": customer.roomType,
        "checkin": customer.checkIn,
        "env": env,
        "prices": prices[customer.rid],
        // "details": details.level === undefined ? "" : details.level.content
        "details": ""
      }));
      if (enoughAvailability(availability)) {
        const baseUrl = getBackendBaseUrl()
        const apiPath = 'api/v1/notifications/batch'
        const url = `${baseUrl}/${apiPath}`;
        fetch(
          url, {
            method: 'POST',
            body: JSON.stringify({notifications: notifications}),
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`,
            }
          }
        ).then(async (response) => {
          if (response.status === 400) {
            setIsOpen(true);
            setIsFailed(true);
            await response.json().then((data) => setMessage(data.message));
            setLoading(false);
          }
          if (response.status === 202) {
            setIsOpen(true);
            setIsFailed(false);
            setMessage("Emails are sent");
            setLoading(false);
          }
        }).catch((err) => {
          setIsOpen(true);
          setIsFailed(true);
          setMessage(err.message);
          setLoading(false);
        });
        handleClickDialogClose();
      } else {
        setIsOpen(true);
        setIsFailed(true);
        setMessage(
          "Can not send email due to: not enough inventory for upsell"
        );
        setLoading(false);
        setShowConfirmationButton(false);
        handleClickDialogClose();
      }
    };

    return (
      <div ref={ref}>
        <div className="modal-header">
          <div className="modal-title">Upgrade Room Type</div>
          <div>
            <span className="close-button" onClick={cancel}>
              x
            </span>
          </div>
        </div>
        <div className="modal-desc">
          <div>
            <div>
              <p>List of reservations to be upgraded:</p>
              <table>
                <thead>
                  <tr>
                    <th>Reservation</th>
                    <th>Customer Name</th>
                    <th>Customer Email</th>
                    <th>Room Type</th>
                    <th>Rate Plan</th>
                    <th>Rate Amount</th>
                    <th>Check In Date</th>
                    <th>Check Out Date</th>
                  </tr>
                </thead>
                <tbody>
                  {selected.map((customer, index) => (
                    <tr key={index}>
                      <td>{customer.rid ? customer.rid : "None"}</td>
                      <td>
                        {customer.name} {customer.surname}
                      </td>
                      <td>{customer.email ? customer.email : "None"}</td>
                      <td>{customer.roomType ? customer.roomType : "None"}</td>
                      <td>{customer.ratePlan ? customer.ratePlan : "None"}</td>
                      <td>
                        {customer.rateAmount ? customer.rateAmount : "None"}
                      </td>
                      <td>{customer.checkIn ? customer.checkIn : "None"}</td>
                      <td>{customer.checkOut ? customer.checkOut : "None"}</td>
                      {
                        // Only include input fields for rooms this type is allowed to upgrade to.
                        to
                        // NOTE: If we want to allow multiple upgrade paths, uncomment below line instead
                          // .filter((toRoom) => {
                          //   return getAllowedUpgrades([customer.roomType]).includes(toRoom.roomType);
                          // })
                          // Add upgrade price to prices
                          .map((roomType, i) => (
                            <td key={`price-${i}`}>

                              <TextField
                                disabled={true}
                                sx={{
                                  mt: 2,
                                  width: "100%",
                                  maxWidth: "250px",
                                  height: "30px",
                                  "& input": {
                                    padding: "0.15rem 0.4rem",
                                    fontSize: "1rem",
                                  },
                                }}
                                label={`${roomType.roomType}`}
                                // Residual now that form fields don't change.
                                // onChange={(e) =>
                                //   handlePriceChange(
                                //     e.value,
                                //     roomType.roomType,
                                //     customer.rid
                                //   )
                                // }
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      $
                                    </InputAdornment>
                                  ),
                                }}
                                type="text"
                                value={lookupUpgradePrice(
                                  customer.roomType,
                                  roomType.roomType
                                )}
                                required={true}
                              />
                            </td>
                          ))
                      }
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <FormControl
            sx={{
              alignItems: "center",
              justifyContent: "center",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {load && <CircularProgress sx={{ mt: 3 }} />}
            {loading && <CircularProgress sx={{ mt: 3 }} />}
          </FormControl>
        </div>
        {/* <DetailsEditor details={details} setDetails={setDetails}/> */}
        <div className="modal-footer">
          <button className="secondary-button" onClick={cancel}>
            Cancel
          </button>
          <Button
            variant={"contained"}
            sx={{
              textTransform: "none",
            }}
            color="primary"
            onClick={handleClickDialogOpen}
            disabled={
              deactivateEmail
              || !selected.every((customer) => Boolean(customer.email))
              || !selected.length
            }
          >
            Send Email
          </Button>
          {
            <Dialog open={open} onClose={handleClickDialogClose}>
              <DialogTitle>Confirm Send Email</DialogTitle>
              <DialogContent>
                Are you sure you would like to send the upgrade emails?
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClickDialogClose} color="primary">
                  Cancel
                </Button>
                <Button
                  variant={"contained"}
                  sx={{
                    textTransform: "none",
                  }}
                  color="primary"
                  onClick={sendEmail}
                  disabled={
                    deactivateEmail
                    // DO NOT SUBMIT
                    // TODO Comment back in
                    // || !selected.every(customer => Boolean(customer.email))
                    || !selected.length
                  }
                >
                  Confirm Send
                </Button>
              </DialogActions>
            </Dialog>
          }
          {/*
          // Upgrade button removed as per requirements.
          <Button
            className="primary-button"
            onClick={() => confirm(mapReservationsToPrices(selected, prices))}
            disabled={deactivateUpgrade}
          >
            Upgrade
          </Button> */}
        </div>
      </div>
    );
  });

export default UpgradeRoom;
